import SectionCard, {
  SectionCardControls,
  SectionCardGroup
} from "../../../../Composites/SectionCard/SectionCard";
import { List, Button, Text } from "@theorgchartdev/storybook";
import React, { useEffect, useState } from "react";
import { AccessGroupModal } from "./AccessGroupModal";
import ActionListItem from "../../../../Composites/ActionListItem/ActionListItem";
import * as StateLib from "@/Utils/StateLib";
import { appstr } from "@coreapp/utils/appstr";
import { gAccessGroupLimitKey } from "@coreapp/whitelabel/brand";
import { getFeatureAccess } from "@/Utils/Misc";
import { enumFeaturesList } from "@coreapp/constants";
import { usePermissionCheck } from "@/hooks/usePermissionCheck";
import { Globals } from "@coreapp/CoreApp/Globals";
import UpgradePopover from "@/appComponents/Blocks/Composites/UpgradePopover/UpgradePopover";

const BuildGroupList = (groups, defaultId, onChange, clickHandler) => {
  let groupOptions = [];

  groupOptions.push({ label: appstr("fullaccess"), divider: true, disabled: true });

  for (let groupID in groups) {
    let group = groups[groupID];

    const groupContent = {
      content: (
        <ActionListItem
          padding="large"
          label={group["name"] ? group["name"] : appstr("Untitled") + " " + groupID}
          actions={[
            {
              iconName: "pen",
              onClick: () => onChange("edit", groupID),
              dataTestId: "account-settings-access-groups-row-edit-btn"
            },
            {
              iconName: groupID === defaultId ? "star-solid" : "star-regular",
              onClick: () => onChange("set-default", groupID),
              dataTestId: "account-settings-access-groups-row-set-default-btn"
            },
            {
              iconName: "copy",
              onClick: () => onChange("copy", groupID),
              dataTestId: "account-settings-access-groups-row-copy-btn"
            },
            {
              iconName: "trash-can",
              iconProps: { color: "error" },
              disabled: groupID === "1",
              onClick: () => onChange("delete", groupID),
              dataTestId: "account-settings-access-groups-row-delete-btn"
            }
          ]}
        />
      ),
      padding: false,
      divider: true,
      value: groupID
    };
    groupOptions.push(groupContent);
  }
  return groupOptions;
};

export const AccessGroups = ({ accountConfig, handleUpdate }) => {
  let accountConfigGroups = accountConfig["security"]["groups"];
  const [showModal, setShowModal] = useState(false);
  const [editGroup, setEditGroup] = useState(null);
  const [newGroupMode, setNewGroupMode] = useState(false);
  let defaultGroup = accountConfig["security"]["userAssignment"]
    ? accountConfig["security"]["userAssignment"]["defaultGroup"]
    : null;

  const wsMgr = StateLib.GetWSMgr();
  const accessGroupMgr = wsMgr.GetAccessGroupMgr();
  const eventTracker = wsMgr.GetUsageTracker();

  useEffect(() => {
    eventTracker.ReportEvent("account_settings.access_groups.opened");
  }, []);

  const { showPricingAndPackaging } = usePermissionCheck();
  let currentEdition = Globals._accountInfo.GetOC5Edition();
  const numUnlimitedAccessGroups = 10000;

  const handleChangesToGroup = (e, groupID) => {
    const updatedGroups = { ...accountConfigGroups };
    switch (e) {
      case "edit":
        setShowModal(true);
        setEditGroup(groupID);
        // return without dirtying state
        return;
      case "set-default":
        accountConfig["security"]["userAssignment"]["defaultGroup"] = groupID;
        break;
      case "delete":
        delete updatedGroups[groupID];
        break;
      case "copy":
        if (showPricingAndPackaging) {
          if (remainingAccessGroups <= 0) {
            break;
          }
        }
        let newID = accessGroupMgr.GetUniqueAccessGroupID();
        let nameList = [];
        let curGroup = accountConfigGroups[groupID];
        for (let i in accountConfigGroups) {
          let aGroup = accountConfigGroups[i];
          nameList.push(aGroup["name"]);
        }
        let newGroupName = nameList.GetUniqueDuplicateName(curGroup["name"]);
        let copyGroup = JSON.parse(JSON.stringify(curGroup));
        copyGroup["name"] = newGroupName;
        updatedGroups[newID] = copyGroup;

        break;
      default:
        break;
    }

    accountConfig["security"]["groups"] = updatedGroups;
    wsMgr.UpdateAccessGroupMgr(accountConfig["security"]);
    handleUpdate(accountConfig["security"], "security");
  };
  const handleListSelect = groupID => {
    setEditGroup(groupID);
  };

  const closeEditGroupModal = (updatedGroup, groupID) => {
    if (updatedGroup && editGroup == groupID) {
      accountConfig["security"]["groups"][editGroup] = updatedGroup;
      wsMgr.UpdateAccessGroupMgr(accountConfig["security"]);
    }
    setShowModal(false);
    setEditGroup(null);
    setNewGroupMode(false);
  };

  const createNewGroup = () => {
    setNewGroupMode(true);
    setShowModal(true);
    let newID = accessGroupMgr.GetUniqueAccessGroupID();
    setEditGroup(newID);
  };

  let usedAccessGroups = Object.keys(accountConfig["security"]["groups"]).length;
  let remainingAccessGroups = Math.max(
    0,
    getFeatureAccess(enumFeaturesList.accessGroupLimit) - usedAccessGroups
  );

  // Determine message based on account edition
  let accessGroupMsg = "";
  if (getFeatureAccess(enumFeaturesList.accessGroupLimit) >= numUnlimitedAccessGroups) {
    // Enterprise and Pro accounts: show used count
    accessGroupMsg = appstr("Access groups used") + ": " + usedAccessGroups;
  } else {
    // Other accounts (plus, premium, core): show remaining count
    accessGroupMsg = appstr("Access groups remaining") + ": " + remainingAccessGroups;
  }

  let agUpgradeMessage = {
    message: appstr("ag upgrade message"),
    template: appstr("ag upgrade template"),
    formatConfig: {
      bold1: { type: "bold" }
    },
    replaceDict: {
      bold1: appstr("granular control")
    }
  };

  const accessGroupButton = () => {
    if (!showPricingAndPackaging) {
      return (
        <Button
          modifier={"secondary"}
          className={"oc-special-secondary"}
          iconProps={{ name: "plus" }}
          onClick={createNewGroup}
          dataTestId={"add-access-group-btn"}
        >
          {appstr("Add Access Group")}
        </Button>
      );
    } else if (
      getFeatureAccess(enumFeaturesList.accessGroupLimit) >= numUnlimitedAccessGroups ||
      remainingAccessGroups > 0
    ) {
      return (
        <Button
          modifier={"secondary"}
          className={"oc-special-secondary"}
          iconProps={{ name: "plus" }}
          onClick={createNewGroup}
          dataTestId={"account-settings-access-groups-add-btn-pnp"}
          disabled={remainingAccessGroups <= 0}
          tooltipProps={
            remainingAccessGroups <= 0
              ? {
                  message: appstr(gAccessGroupLimitKey),
                  position: "right-end"
                }
              : null
          }
        >
          {appstr("Add Access Group")}
        </Button>
      );
    } else {
      return (
        <UpgradePopover
          popoverText={agUpgradeMessage}
          featureKey={enumFeaturesList.accessGroupLimit}
        />
      );
    }
  };

  return (
    <SectionCardGroup>
      <SectionCardControls>{accessGroupButton()}</SectionCardControls>
      {(editGroup != null || newGroupMode) && (
        <AccessGroupModal
          accountConfig={accountConfig}
          groups={accountConfigGroups}
          show={showModal}
          existingGroup={editGroup}
          isCreateNewGroup={newGroupMode}
          onClose={closeEditGroupModal}
        />
      )}
      {showPricingAndPackaging && (
        <Text type={"body"} size={"small"}>
          {accessGroupMsg}
        </Text>
      )}
      <SectionCard title={appstr("Groups")}>
        <List
          enableVirtualization={false}
          options={BuildGroupList(
            accountConfigGroups,
            defaultGroup,
            handleChangesToGroup,
            handleListSelect
          )}
          listWidth="fill"
          optionSize={"large"}
          showCheck={false}
          value={null}
        />
      </SectionCard>
    </SectionCardGroup>
  );
};
